import { FC, useState } from 'react';
import { HarmReportDetailsProps } from './harmReports.type';
import ReportDetails from '../../component/reportDetails/reportDetails';
import { createHarmReportConfig } from '../../component/reportDetails/harmReportConfig';
import { getHarmReportsKebabOptions } from './harmReportesHelper';
import { InterventionReportDetails } from '../interventionReports/interventionReportDetails';
import { InterventionReportModel } from '../../models/interventionReportModel';
import { reportService } from '../../service/instances';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import {
  getHarmReportEditPermissions,
  INTERVENTION_DURATION_MS,
  MANAGERS_REVIEW_DURATION_MS,
} from '../../utils/harmReportEditPermissions';
import ModalComponent from '../../component/modal/modal';
import Loading from '../../assets/loading/loading';

export const HarmReportDetails: FC<HarmReportDetailsProps> = (props) => {
  const { harmReport } = props;
  const config = createHarmReportConfig(harmReport);
  const { selectedVenueBranch } = useOrganizationContext();
  const { userDetail } = useUserDetailContext();
  const [viewingIntervention, setViewingIntervention] =
    useState<InterventionReportModel | null>(null);
  const [loadingLinkedReport, setLoadingLinkedReport] =
    useState<boolean>(false);

  const editDurationOptions = {
    interventionDurationMs: selectedVenueBranch?.interventionDurationMinutes
      ? selectedVenueBranch.interventionDurationMinutes * 60 * 1000
      : INTERVENTION_DURATION_MS,
    managersReviewDurationMs: selectedVenueBranch?.managersReviewDurationMinutes
      ? selectedVenueBranch.managersReviewDurationMinutes * 60 * 1000
      : MANAGERS_REVIEW_DURATION_MS,
  };

  const handleLinkedReportClick = async (reportId: string) => {
    if (!selectedVenueBranch) return;
    setLoadingLinkedReport(true);
    try {
      const reports: InterventionReportModel[] =
        await reportService.getInterventionReports({
          venueBranchId: selectedVenueBranch._id,
          currentDate: new Date(harmReport.createdAt),
        });
      const found: InterventionReportModel | undefined = reports.find(
        (report: InterventionReportModel) =>
          report.originalReportId === reportId || report.id === reportId,
      );
      if (found) setViewingIntervention(found);
    } catch (error) {
      console.error(
        '[HarmReportDetails] Error fetching intervention report',
        error,
      );
    } finally {
      setLoadingLinkedReport(false);
    }
  };

  return (
    <>
      <ReportDetails
        config={config}
        onClose={props.closeHarmReportDetails}
        onReviewAndSign={props.handleReviewAndSignClick}
        kebabOptions={getHarmReportsKebabOptions(
          harmReport,
          null,
          0,
          getHarmReportEditPermissions(
            harmReport,
            userDetail?.type,
            userDetail?.id,
            editDurationOptions,
          ).canEdit,
          userDetail?.type,
          userDetail?.id,
        )
          .filter((o) => o.label !== 'View')
          .map((o) => ({
            label: o.label,
            optionKey: o.label,
          }))}
        onKebabAction={props.onKebabAction}
        onLinkedReportClick={
          props.onKebabAction ? handleLinkedReportClick : undefined
        }
      />
      {loadingLinkedReport && !viewingIntervention && (
        <ModalComponent
          onToggle={() => setLoadingLinkedReport(false)}
          open={true}
          title=""
          isShowClose
          footerHidden
          dialogClassName="w-full max-w-modal-lg h-[90vh] overflow-hidden"
        >
          <div className="flex justify-center items-center h-[calc(90vh-100px)]">
            <Loading />
          </div>
        </ModalComponent>
      )}
      {viewingIntervention && (
        <InterventionReportDetails
          interventionReport={viewingIntervention}
          onClose={() => setViewingIntervention(null)}
          onReviewAndSign={() => setViewingIntervention(null)}
        />
      )}
    </>
  );
};
